import React from "react";
import { Form } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { useDeleteAccount } from "@admin/hooks/useDeleteAccount";
import { Typography, useTheme } from "@mui/material";
import { DangerButton } from "@shared/CustomButton";
import { ControlledCheckboxField } from "@shared/form/CheckboxField";
import { RadioField, RadioFieldGorup } from "@shared/form/RadioField";
import { Modal } from "@shared/Modal/Modal";

const useStyles = makeStyles()({
    email: {
        // An email address offers no break opportunity, so without this it overflows the dialog
        // instead of wrapping.
        overflowWrap: "anywhere",
    },
});

type FormValues = {
    mode: "soft" | "full";
    sendNotification: boolean;
};

type Props = {
    account: API.Account;
    onClose: () => void;
    onDeleted: () => void;
};

export const DeleteAccountDialog = ({ account, onClose, onDeleted }: Props) => {
    const { t } = useTranslation(["modals", "common"]);
    const { classes } = useStyles();
    const theme = useTheme();
    const { isLoading, onMutate } = useDeleteAccount();
    const [isConfirmationStep, setIsConfirmationStep] = React.useState(false);

    // Closing an already anonymised account is a silent no-op on the backend (it reports success
    // without touching anything), so a permanent deletion is the only option left for such a row.
    const isAlreadyDeleted = Boolean(account.deleted);

    const onCloseRequest = () => {
        if (!isLoading) {
            onClose();
        }
    };

    const onSubmit = (values: FormValues) => {
        const payload = {
            id: account.id,
            fullDeletion: values.mode === "full",
            sendNotification: values.sendNotification,
        };

        onMutate(payload, () => {
            onClose();
            onDeleted();
        });
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            initialValues={{ mode: isAlreadyDeleted ? "full" : "soft", sendNotification: !isAlreadyDeleted }}
            render={({ form, values }) => {
                // A permanent deletion cannot be undone, so it only submits from the second step.
                const onPrimaryAction = () => {
                    if (values.mode === "full" && !isConfirmationStep) {
                        setIsConfirmationStep(true);
                        return;
                    }
                    void form.submit();
                };

                return (
                    <Modal
                        open
                        onClose={onCloseRequest}
                        title={t("modals:adminDeleteAccount.title")}
                        secondaryActionText={t("common:button.cancel")}
                        description={
                            isConfirmationStep ? undefined : (
                                <Trans
                                    ns="modals"
                                    i18nKey="adminDeleteAccount.description"
                                    values={{ email: account.email }}
                                    components={{ bold: <strong className={classes.email} /> }}
                                />
                            )
                        }
                        endButton={
                            <DangerButton onClick={onPrimaryAction} disabled={isLoading} loading={isLoading}>
                                {isConfirmationStep ? t("common:button.confirm") : t("common:button.delete")}
                            </DangerButton>
                        }
                    >
                        {isConfirmationStep ? (
                            <Typography variant="body1">
                                <Trans
                                    ns="modals"
                                    i18nKey="adminDeleteAccount.confirm.description"
                                    values={{ email: account.email }}
                                    components={{ bold: <strong className={classes.email} /> }}
                                />
                            </Typography>
                        ) : (
                            <React.Fragment>
                                <RadioFieldGorup name="mode">
                                    <RadioField
                                        value="soft"
                                        label={t("modals:adminDeleteAccount.input.softDeletion.caption")}
                                        helperText={t("modals:adminDeleteAccount.input.softDeletion.helperText")}
                                        style={{ marginBottom: theme.spacing(5) }}
                                        disabled={isAlreadyDeleted}
                                        tooltip={isAlreadyDeleted ? t("modals:adminDeleteAccount.tooltip.alreadyDeleted") : ""}
                                    />

                                    <RadioField
                                        value="full"
                                        label={t("modals:adminDeleteAccount.input.fullDeletion.caption")}
                                        helperText={t("modals:adminDeleteAccount.input.fullDeletion.helperText")}
                                        style={{ marginBottom: theme.spacing(4) }}
                                    />
                                </RadioFieldGorup>

                                <ControlledCheckboxField
                                    name="sendNotification"
                                    label={t("modals:adminDeleteAccount.input.notify.caption")}
                                    disabled={isAlreadyDeleted}
                                    helperText={
                                        isAlreadyDeleted ? t("modals:adminDeleteAccount.input.notify.helperText") : undefined
                                    }
                                />
                            </React.Fragment>
                        )}
                    </Modal>
                );
            }}
        />
    );
};
